const { jQuery: $ } = window;

/**
 * Thin REST client over jQuery.ajax with the plugin's before/after action hooks, plus the higher-level
 * getPosts() flow that fetches a page of posts and updates the instance's state. The in-flight request is
 * tracked per ShortcodeInstance (instance.currentRequest), so aborting one shortcode never cancels another's.
 */
export default class RestClient {
    /** @member {Hooks} */
    #hooks;

    /** @member {Logger} */
    #logger;

    /** @member {object} Page-level settings ({restBase, ajax}). */
    #settings;

    /**
     * @param {Hooks} hooks
     * @param {Logger} logger
     * @param {object} settings Page-level settings object (shares restBase + ajax defaults).
     */
    constructor(hooks, logger, settings) {
        this.#hooks = hooks;
        this.#logger = logger;
        this.#settings = settings;
    }

    /**
     * Standardized jQuery.ajax() wrapper with generic + endpoint-specific before/after hooks.
     *
     * @param {ShortcodeInstance} instance The instance whose request slot is used for abort tracking.
     * @param {string} endpoint REST endpoint name (e.g. "posts", "filters").
     * @param {object} data Query-string data.
     * @param {string} slot Instance property used to track/abort this request. Defaults to "currentRequest"
     *                      (posts). Filter-data uses a SEPARATE slot so the parallel /posts + /filters
     *                      requests on a filter change never abort each other.
     * @returns {jQuery.jqXHR}
     */
    fetch(instance, endpoint, data = {}, slot = "currentRequest") {
        const $ajaxOpts = $.extend({
            data: data,
            method: "GET",
            beforeSend: (jqXHR) => {
                this.#hooks.doAction(`ajax_loading_${endpoint}`, jqXHR, data);
                this.#hooks.doAction("ajax_loading", endpoint, jqXHR, data);
                instance[slot] = jqXHR;
            }
        }, this.#settings.ajax);

        const settings = this.#hooks.applyFilter(`ajax_settings_${endpoint}`, $ajaxOpts, data);

        return $.ajax(`${this.#settings.restBase}/${endpoint}`, settings)
            .always((response) => {
                instance[slot] = null;
                this.#hooks.doAction(`ajax_finished_${endpoint}`, response, data);
                this.#hooks.doAction("ajax_finished", endpoint, response, data);
            });
    }

    /**
     * Retrieves posts for a shortcode. Filter post_request_settings to change the request, and
     * get_posts_response to change the server response before it is processed. Fires get_posts_success /
     * get_posts_aborted / get_posts_failed.
     *
     * @param {ShortcodeInstance} instance
     * @param {int} count Number of posts to request.
     * @param {Function} callback Invoked with (posts, meta) on success.
     * @param {object} [opts]
     * @param {boolean} [opts.background=false] Suppress the loading indicator — for the history-cache prime,
     *                                          which fetches silently while the server-rendered posts stay.
     */
    getPosts(instance, count, callback, { background = false } = {}) {
        if(instance.currentRequest) {
            instance.currentRequest.abort();
        }

        const store = instance.store;
        const $el = instance.$el;
        const settings = store.get();

        // Visible "refreshing" state: dim the current results + show a spinner (see _loading.scss) until the
        // request settles. Covers every USER-initiated /posts fetch (filter/search/sort/paginate/load-more).
        // Skipped for background fetches (the history-cache prime), where the server-rendered posts are already
        // on screen and unchanged — a dim + spinner there would be misleading.
        if(!background) {
            $el.addClass("kpfsc--loading");
        }

        this.fetch(instance, "posts", {
            "request": JSON.stringify(this.#hooks.applyFilter("post_request_settings", settings)),
            "count": count
        }).done((response) => {
            this.#logger.start("Get Posts")
                .groupStart("Request", "Data", this.#logger.COLOR.PURPLE)
                .dir(settings)
                .groupEnd()
                .groupStart("Response", "Metadata", this.#logger.COLOR.PURPLE)
                .table(response.meta)
                .groupEnd()
                .groupStart("Response", "Posts", this.#logger.COLOR.PURPLE)
                .dir(response.posts)
                .groupEnd()
                .end();

            response = this.#hooks.applyFilter("get_posts_response", response, settings, $el);

            store.set("queryMeta", response.meta);

            // Mirror the server-side $moreClass exactly: the load-more button is hidden when load-more is
            // disabled (showMore=false) OR there are no further pages. Without the showMore guard, a fetch
            // would un-hide a button the server intentionally hid — making it pop in after a filter change.
            if(!store.get("showMore") || response.meta.pages <= store.get("filterPage")) {
                $el.addClass("kpfsc--no-more");
            } else {
                $el.removeClass("kpfsc--no-more");
            }

            if(response.posts.length === 0) {
                $el.addClass("kpfsc--no-posts");
            } else {
                $el.removeClass("kpfsc--no-posts");
            }

            // History state management
            const selected = store.get("filterSelected");
            const defaults = store.get("defaults");

            store.cache.push(...response.posts);

            // Require reload if current selections differ from the defaults.
            for(let i in selected) {
                const defaultSelected = Array.isArray(defaults.filterSelected[i]) ? defaults.filterSelected[i] : [defaults.filterSelected[i]];
                const activeSelected = Array.isArray(selected[i]) ? selected[i] : [selected[i]];

                const filtersWithDefaultVals = defaultSelected.filter(x => activeSelected.includes(x));

                if(filtersWithDefaultVals.length !== activeSelected.length) {
                    store.requiresReload = true;
                    break;
                }
            }

            // Reload pagination based on new settings
            instance.loadPagination();

            // Refresh the applied-filter chips + results count from the new state/meta.
            instance.loadAppliedFilters();

            if(callback) callback.call(instance, response.posts, response.meta);

            // Save current history state
            instance.saveState();

            this.#hooks.doAction("get_posts_success", response, settings, $el);

        }).fail((jqXHR, textStatus, errorThrown) => {

            if(textStatus === "abort") {
                this.#logger.warn("Get Posts: Aborted");
                this.#hooks.doAction("get_posts_aborted", jqXHR, textStatus, errorThrown, settings, $el);
            } else {
                this.#logger.start("Get Posts")
                    .log("jqXHR:", jqXHR)
                    .log("textStatus:", textStatus)
                    .error("Error:", errorThrown)
                    .end();

                this.#hooks.doAction("get_posts_failed", jqXHR, textStatus, errorThrown, settings, $el);
            }

        }).always(() => {
            // Clear the refreshing state, unless it was suppressed (background) or a newer /posts request has
            // already superseded this one (rapid filter/search changes abort the in-flight request).
            if(!background && !instance.currentRequest) {
                $el.removeClass("kpfsc--loading");
            }
        });
    }

    /**
     * Fetches batch filter-data (narrowed taxonomy options and/or disjunctive per-option counts) for the
     * shortcode's current query state and hands it to the instance's applyFilterData(). One request covers
     * every filter regardless of type or UI.
     *
     * Uses a SEPARATE in-flight slot (instance.currentFilterRequest) so it can run in parallel with the
     * /posts request on a filter change without either aborting the other. A newer filter-data request
     * still supersedes an older one (the last selection wins).
     *
     * @param {ShortcodeInstance} instance
     * @param {object} opts { withOptions, withCounts } — which parts of the payload to request.
     * @param {Function} [callback] Invoked with the filter-data map on success.
     */
    getFilterData(instance, {withOptions = false, withCounts = false} = {}, callback) {
        if(instance.currentFilterRequest) {
            instance.currentFilterRequest.abort();
        }

        const store = instance.store;
        const $el = instance.$el;
        const settings = store.get();

        this.fetch(instance, "filters", {
            "request": JSON.stringify(this.#hooks.applyFilter("post_request_settings", settings)),
            "options": withOptions ? 1 : 0,
            "counts": withCounts ? 1 : 0
        }, "currentFilterRequest").done((response) => {
            this.#logger.start("Get Filter Data")
                .groupStart("Response", "Filter Data", this.#logger.COLOR.PURPLE)
                .dir(response)
                .groupEnd()
                .end();

            response = this.#hooks.applyFilter("get_filters_response", response, settings, $el);

            instance.applyFilterData(response || {});

            if(callback) callback.call(instance, response);

            this.#hooks.doAction("get_filters_success", response, settings, $el);

        }).fail((jqXHR, textStatus, errorThrown) => {

            if(textStatus === "abort") {
                this.#logger.warn("Get Filter Data: Aborted");
                this.#hooks.doAction("get_filters_aborted", jqXHR, textStatus, errorThrown, settings, $el);
            } else {
                this.#logger.start("Get Filter Data")
                    .log("jqXHR:", jqXHR)
                    .log("textStatus:", textStatus)
                    .error("Error:", errorThrown)
                    .end();

                this.#hooks.doAction("get_filters_failed", jqXHR, textStatus, errorThrown, settings, $el);
            }

        });
    }
}
